/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useProductDetails } from '@/features/product/api/getProductDetails';
import { type ReviewsResponse, type ReviewType } from '../interface/types';
import { selector as mapApiToReviewResponse, useGetReviews } from '../api/getReviews';

export type ReviewPhotoMap = {
  [key: string]: string[];
};
type ReviewSortAttribute = 'MOST_RECENT' | 'MOST_HELPFUL' | 'MOST_RELEVANT';

type ReviewFilterParams = {
  showReviewsRated1Star?: boolean;
  showReviewsRated2Star?: boolean;
  showReviewsRated3Star?: boolean;
  showReviewsRated4Star?: boolean;
  showReviewsRated5Star?: boolean;
  showReviewsFromVerifiedPurchasers?: boolean;
  sortAttribute?: ReviewSortAttribute;
  searchText?: string;
  showReviewsWithImagesOnly?: boolean;
};

export type ReviewImageResult = {
  reviews: ReviewType[] | null;
  imageUrls: string[] | undefined;
  photoCaption: string[] | null;
  photoMap: ReviewPhotoMap;
};

export function useProductReviewImages(
  filterParams: ReviewFilterParams
): ReviewImageResult | undefined {
  const { data: productDetails } = useProductDetails();
  const skuId = productDetails?.product?.itemId ?? '';

  const { data } = useGetReviews({
    skuNumber: skuId,
    enabled: !!skuId,
    showReviewsWithImagesOnly: true,
    ...filterParams,
    select: (reviewsResponse: ReviewsResponse) =>
      selectImages(mapApiToReviewResponse(reviewsResponse)),
  });

  return data;
}

const selectImages = (reviews: ReviewType[] | null | undefined) => {
  if (!reviews) {
    return {
      reviews: null,
      imageUrls: [],
      photoCaption: [],
      photoMap: {},
    };
  }

  const imageUrls: string[] = [];
  const photoCaption: string[] = [];
  const photoMap: ReviewPhotoMap = {};

  for (const review of reviews) {
    const reviewPhotos = review.reviewPhotos;
    if (!reviewPhotos) continue;

    const reviewPhotoUrls: string[] = [];

    for (const photo of reviewPhotos) {
      const normalSizeUrl = photo?.photoSizes?.find(
        (size: { photoSizeId: string }) => size.photoSizeId === 'normal'
      )?.photoUrl;

      if (normalSizeUrl && normalSizeUrl !== '') {
        imageUrls.push(normalSizeUrl);
        reviewPhotoUrls.push(normalSizeUrl);
      }

      if (photo?.photoCaption) {
        photoCaption.push(photo.photoCaption);
      }
    }

    if (reviewPhotoUrls.length > 0) {
      photoMap[review.reviewId] = reviewPhotoUrls;
    }
  }

  return {
    reviews,
    imageUrls,
    photoCaption,
    photoMap,
  };
};
